import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Cached sticky-activation threshold (header height + notification bar height).
   * Recomputed only when those elements resize, not on every scroll tick.
   * @type {number}
   */
  #stickyThreshold = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  /**
   * Updates the global CSS variables for header height, including any sticky bars.
   */
  #updateHeaderHeight = () => {
    const headerHeight = this.offsetHeight;
    let totalHeight = headerHeight;

    const popup = document.querySelector('.main-top-notification-bar');
   
    if (popup) {
      totalHeight += popup.offsetHeight;
      document.body.style.setProperty('--announcement-bar-height', `${popup.offsetHeight}px`);
    } else {
      document.body.style.setProperty('--announcement-bar-height', '0px');
    }
    
    // const announcementPopup = document.querySelector('.announcement-bar-popup');
    // if (announcementPopup) totalHeight += announcementPopup.offsetHeight;

    if(window.innerWidth > 749){
      // console.log('-----',totalHeight,'here',totalHeight,popup.offsetHeight);
      document.body.style.setProperty('--header-height', `${totalHeight}px`);
    }else{
      document.body.style.setProperty('--header-height', `${totalHeight + document.querySelector('.predictive-search-form__header').offsetHeight}px`);
    }
    document.body.style.setProperty('--header-actual-height', `${headerHeight}px`);
  };

  /**
   * Recomputes and caches the sticky-activation threshold (header height plus any
   * sticky bars above it). Called only when those elements resize, so scroll handling
   * can read the cached value instead of forcing a layout on every tick.
   */
  #updateStickyThreshold = () => {
    let threshold = this.offsetHeight;
    const notificationBar = document.querySelector('.main-top-notification-bar');
    if (notificationBar) threshold += notificationBar.offsetHeight;
    this.#stickyThreshold = threshold;
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(() => {
    this.#updateHeaderHeight();
    this.#updateStickyThreshold();

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  static #SCROLL_THRESHOLD = 5;

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = window.pageYOffset;

    if (this.hasAttribute('data-prevent-scroll-sticky')) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    const delta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = delta < 0;

    const threshold = this.#stickyThreshold;


    // 1. Condition: Set "inactive" and "none" until threshold is reached, 
    // unless the header is already in a sticky active state
    if (scrollTop <= 0 || (scrollTop < threshold && this.dataset.stickyState !== 'active')) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = Math.max(0, scrollTop);
      return;
    }

    // Jitter fix
    if (Math.abs(delta) < HeaderComponent.#SCROLL_THRESHOLD) return;

    if (stickyMode === 'scroll-up') {
      if (isScrollingUp) {
        // 3. Condition: on scroll up set until first condition reached
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      } else {
        // 2. Condition: when header is scrolled more than header height
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'none';
      }
    }

    if (stickyMode === 'always') {
      const headerTop = this.getBoundingClientRect().top;
      if (headerTop >= 0) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }
    }

    this.#lastScrollTop = scrollTop;
  };


  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#updateStickyThreshold();

      if (stickyMode === 'always') {
        this.#observeStickyPosition(true);
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }

    // Update combined height when announcement bar sticky state toggles
    const popup = document.querySelector('.announcement-bar-popup');
    if (popup) {
      this.#resizeObserver.observe(popup);
      const popupParent = popup.parentElement;
      if (popupParent) {
        const heightSyncObserver = new MutationObserver(() => this.#updateHeaderHeight());
        heightSyncObserver.observe(popupParent, { attributes: true, attributeFilter: ['class'] });
      }
    }

    const notificationBar = document.querySelector('.main-top-notification-bar');
    if (notificationBar) {
      this.#resizeObserver.observe(notificationBar);
    }
  }



  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});


document.querySelector('.alp-toggle')?.addEventListener('click', function () {
  document.body.classList.toggle('alp-active');
}); 


if (typeof window.updateProductCard !== 'function') {
  window.updateProductCard = function(el) {
    if (!el.dataset.url) return;

    // 1. Handle Quick Add Dialog
    const dialog = el.closest('quick-add-dialog');
    if (dialog) {
      dialog.dispatchEvent(new CustomEvent('quick-add:load-product', {
        detail: { url: el.dataset.url },
        bubbles: true
      }));
      return;
    }

    // 2. Handle Product Page
    const productInfoGrid = el.closest('.product-information__grid');
    const productInfo = el.closest('.product-information');
    if (productInfoGrid && productInfo && !el.closest('product-card')) {
      const mainContent = document.getElementById('MainContent') || document.querySelector('main');
      if (mainContent) {
        const url = new URL(el.dataset.url, window.location.origin);

        mainContent.style.opacity = '0.5';
        mainContent.style.pointerEvents = 'none';

        fetch(url.toString())
          .then(response => response.text())
          .then(htmlText => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, 'text/html');
            const newMainContent = doc.getElementById('MainContent') || doc.querySelector('main');

            if (newMainContent) {
              // Replace the entire main content
              mainContent.innerHTML = newMainContent.innerHTML;

              // Re-execute scripts that might have been injected
              const scripts = mainContent.querySelectorAll('script');
              scripts.forEach(script => {
                if (script.type === 'application/json') return;
                const newScript = document.createElement('script');
                Array.from(script.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
                newScript.appendChild(document.createTextNode(script.innerHTML));
                script.parentNode.replaceChild(newScript, script);
              });

              // Update URL without refresh
              window.history.pushState({}, '', el.dataset.url);

              // Re-initialize all sections inside main
              const sections = mainContent.querySelectorAll('.shopify-section');
              sections.forEach(section => {
                const sectionId = section.id.replace('shopify-section-', '');
                document.dispatchEvent(new CustomEvent('shopify:section:load', {
                  detail: { sectionId: sectionId }
                }));
              });

              // Refresh Yotpo widgets for newly loaded product
              if (typeof refreshYotpoWidgets === 'function') {
                refreshYotpoWidgets();
              } else {
                const doRefresh = () => {
                  try {
                    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
                      window.yotpoWidgetsContainer.initWidgets();
                    }
                    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.init === 'function') {
                      window.yotpoWidgetsContainer.init();
                    }
                    if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
                      window.yotpo.refreshWidgets();
                    }
                    if (window.yotpo && typeof window.yotpo.initWidgets === 'function') {
                      window.yotpo.initWidgets();
                    }
                    if (window.yotpo && typeof window.yotpo.init === 'function') {
                      window.yotpo.init();
                    }
                    if (window.Yotpo && typeof window.Yotpo.reload === 'function') {
                      window.Yotpo.reload();
                    }
                    if (window.Yotpo && typeof window.Yotpo.initWidgets === 'function') {
                      window.Yotpo.initWidgets();
                    }
                  } catch (err) {
                    console.warn('Yotpo refresh error:', err);
                  }
                };
                doRefresh();
                setTimeout(doRefresh, 100);
                setTimeout(doRefresh, 500);
              }
            }
          })
          .catch(err => {
            console.error('Error switching product:', err);
            window.location.href = el.dataset.url;
          })
          .finally(() => {
            mainContent.style.opacity = '';
            mainContent.style.pointerEvents = '';
          });
        return;
      }
    }

    // 3. Handle Product Card
    const card = el.closest('product-card') || el.closest('.ss-product-card') || el.closest('[data-ss-product]');
    // Note: In SearchSpring templates, the container may be slightly different than product-card (e.g. .product-card).
    // Let's resolve the actual card container dynamically.
    const resolvedCard = card || el.closest('.product-card') || el.closest('.grid__item');
    if (!resolvedCard) return;

    const swymEl = resolvedCard.querySelector('.swym-wishlist-collections-v2');

    // Get saved transform
    let originalTransform = '';
    if (swymEl) {
      originalTransform = swymEl.getAttribute('data-original-transform');
    }

    // 1. Manage Active Class
    const swatches = resolvedCard.querySelectorAll('.pcs-block__swatch');
    swatches.forEach(s => s.classList.remove('pcs-block__swatch--active'));
    el.classList.add('pcs-block__swatch--active');

    // 2. Update Title
    const titleLink = resolvedCard.querySelector('a[ref="productTitleLink"]') || resolvedCard.querySelector('.product-card__title a') || resolvedCard.querySelector('.card__heading a');

    if (titleLink && el.dataset.title) {
      const titleText = titleLink.querySelector('.text-block p, .text-block h1, .text-block h2, .text-block h3');
      if (titleText) {
        titleText.textContent = el.dataset.title;
      } else {
        // Fallback if structure is different
        const p = titleLink.querySelector('p') || titleLink;
        if (p) p.textContent = el.dataset.title;
      }
    }

    // 3. Update Vendor
    const vendorEl = resolvedCard.querySelector('.product-vendor');
    if (vendorEl && el.dataset.vendor) {
      vendorEl.textContent = el.dataset.vendor;
    }

    // 4. Update Price
    const priceWrapper = resolvedCard.querySelector('product-price') || resolvedCard.querySelector('.price');
    if (priceWrapper) {
      let priceHtml = '';
      if (el.dataset.compare) {
        priceHtml = `<div class="price price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden">Sale price</span>
              <span class="price-item price-item--sale price-item--last">${el.dataset.price}</span>
              <span class="visually-hidden">Regular price</span>
              <span><s class="price-item price-item--regular">${el.dataset.compare}s</s></span>
            </div>
          </div>
        </div>`;
      } else {
        priceHtml = `<div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden">Regular price</span>
              <span class="price-item price-item--regular">${el.dataset.price}</span>
            </div>
          </div>
        </div>`;
      }
      const innerPrice = priceWrapper.querySelector('.price');
      if (innerPrice) {
        innerPrice.outerHTML = priceHtml;
      } else {
        priceWrapper.innerHTML = priceHtml;
      }
    }

    // 5. Update Color Label
    const colorLabel = resolvedCard.querySelector('[ref="colorLabel"]') || resolvedCard.querySelector('.pcs-block__color-label') || resolvedCard.querySelector('.product-card-color-swatches__label');
    if (colorLabel && (el.dataset.variantName || el.dataset.colorGroup)) {
      colorLabel.textContent = `Colour: ${el.dataset.variantName || el.dataset.colorGroup}`;
    }

    // 6. Update Media Slider
    const slideshow = resolvedCard.querySelector('slideshow-component');
    if (slideshow && el.dataset.media) {
      const mediaUrls = JSON.parse(el.dataset.media);
      const scroller = slideshow.querySelector('slideshow-slides');

      if (scroller && mediaUrls.length > 0) {
        scroller.innerHTML = ''; // Full clear

        mediaUrls.forEach((url, i) => {
            const slide = document.createElement('slideshow-slide');
            slide.setAttribute('ref', 'slides[]');
            slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
            slide.setAttribute('index', i);
            slide.className = 'product-media-container media-fit';
            slide.style.cssText = `--slideshow-timeline: --slide-${i}; --product-media-fit: cover;`;

            slide.innerHTML = `<img src="${url}" alt="${el.dataset.title || ''}" loading="${i === 0 ? 'eager' : 'lazy'}" style="width: 100%; height: 100%; object-fit: cover; aspect-ratio: 4/5;">`;
            scroller.appendChild(slide);
        });

        // Re-trigger scroll snap
        scroller.scrollTo({ left: 0, behavior: 'auto' });

        // Force thumbnail progress update manually before clone
        const thumb = slideshow.querySelector('.slideshow-scrollbar__thumb');
        if (thumb) {
          thumb.style.setProperty('--scroll-progress', (1 / mediaUrls.length).toFixed(4));
        }

        // Force re-initialization of the slideshow-component
        // to update arrows, scroll physics, and refs
        const newSlideshow = slideshow.cloneNode(true);
        slideshow.parentNode.replaceChild(newSlideshow, slideshow);

        if (resolvedCard.refs) {
            resolvedCard.refs.slideshow = newSlideshow;
        }

        // Re-init the custom scrollbar script for the new slideshow
        if (typeof initScrollbar === 'function') {
          initScrollbar(newSlideshow);
        } else {
            const newScroller = newSlideshow.querySelector('slideshow-slides');
            const newThumb = newSlideshow.querySelector('.slideshow-scrollbar__thumb');
            if (newScroller && newThumb) {
              const updateProgress = () => {
                const slides = newScroller.children.length;
                if (slides <= 0) return newThumb.style.setProperty('--scroll-progress', 0);
                const maxScroll = newScroller.scrollWidth - newScroller.clientWidth;
                let scrollProgress = maxScroll > 0 ? newScroller.scrollLeft / maxScroll : 0;
                const base = 1 / slides;
                const progress = base + scrollProgress * (1 - base);
                newThumb.style.setProperty('--scroll-progress', progress.toFixed(4));
              };
              newScroller.addEventListener('scroll', updateProgress, { passive: true });
              requestAnimationFrame(updateProgress);
            }
        }
      }
    } else if (el.dataset.media) {
        // Fallback if slideshow doesn't exist or is a simple image
        const mediaUrls = JSON.parse(el.dataset.media);
        if (mediaUrls.length > 0) {
          // Find image tags inside the card gallery
          const cardGallery = resolvedCard.querySelector('.card-gallery') || resolvedCard.querySelector('.product-card__image-container') || resolvedCard.querySelector('.card__media');
          if (cardGallery) {
              const firstImg = cardGallery.querySelector('img');
              if (firstImg) {
                firstImg.src = mediaUrls[0];
                firstImg.srcset = '';
              }
          }
        }
    }

    // 7. Update Link
    const links = resolvedCard.querySelectorAll('a[href]');
    links.forEach(l => {
      if (el.dataset.url && !l.classList.contains('pcs-block__swatch')) {
        l.href = el.dataset.url;
      }
    });

    const cardLink = resolvedCard.querySelector('input[ref="productCardLink"]');
    if (cardLink) {
      cardLink.value = el.dataset.url;
    }

    if (swymEl && originalTransform) {
      swymEl.style.setProperty('transform', originalTransform, 'important');
    }
  };
}

// Add a global click delegator on the document for SearchSpring swatches
document.addEventListener('click', function(event) {
  if (event.target instanceof Element) {
    const swatch = event.target.closest('.pcs-block__swatch');
    if (swatch) {
      window.updateProductCard(swatch);
    }
  }
});